import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import { chevronDownOutline, chevronUpOutline, searchOutline } from "ionicons/icons";
import { FC, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import styled from "styled-components";
import { cardSurface } from "../../components/atoms/Card";
import Icon from "../../components/atoms/Icon";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../navigation/studyTabs";
import { getSuiviGroups, SuiviGroup, SuiviGroupsResponse } from "../../services/suivi.service";
import { getWelcomeInfo } from "../../services/weclome.service";
import { canManageDossiers } from "../../helper/familyComposition";
import { CreateDossierModal } from "./DossierModals";
import { RoleEnum } from "../../types/invitation.types";
import dayjs from "dayjs";
import {
  CaptionLine,
  Chevron,
  DueChip,
  EmptyText,
  FooterLine,
  ListCard,
  OutlinePill,
  Page,
  Row,
  RowEnd,
  RowMain,
  RowSub,
  RowTitle,
  SectionLabel,
} from "./suiviUi";

/**
 * Suivi home — the investigator dashboard's landing. Design contract:
 * one caption sentence is the ENTIRE analytics layer (no KPI tiles/charts),
 * the search pill live-filters the list, groups needing attention lead with a
 * derived action line + the group's WORST-urgency deadline chip, up-to-date
 * groups stay folded. No tab badge, no filter chips.
 */
const SuiviHome: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { study, selectedRole } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();

  const [data, setData] = useState<SuiviGroupsResponse | null>(null);
  const [isError, setIsError] = useState(false);
  const [query, setQuery] = useState("");
  const [upToDateOpen, setUpToDateOpen] = useState(false);
  // « Ajouter un dossier » — capability-gated: only when the study has data-only
  // subject roles (from the cached welcome catalog). MentalPlus never shows it.
  const [canCreateDossier, setCanCreateDossier] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchGroups = useCallback((): void => {
    if (!study?.id) return;
    // Never fire the investigator-only request while the self-guard below is
    // redirecting a known non-investigator away (effects run even when the
    // render returned a <Redirect>).
    if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) return;
    getSuiviGroups({ studyId: study.id, lang: language })
      .then(({ data: response }) => {
        setIsError(false);
        setData(response);
      })
      .catch(() => setIsError(true));
  }, [study?.id, language, selectedRole]);

  // Refetch on EVERY tab entry (stamps/colleague completions must show up):
  // useIonViewWillEnter registers once, so route the call through a ref that is
  // reassigned each render (the DisplayIsland trampoline pattern).
  const willEnterRef = useRef<(() => void) | undefined>(undefined);
  willEnterRef.current = () => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "suivi_tab_label",
      welcomePage: false,
      headerLeft: undefined, // project default
    });
    fetchGroups();
  };
  useIonViewWillEnter(() => {
    willEnterRef.current?.();
  });

  // First mount also fetches (ionViewWillEnter fires, but keep language changes live).
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    let cancelled = false;
    if (!study_slug || !selectedRole) return undefined;
    getWelcomeInfo({ slug: study_slug, lang: language })
      .then(({ data: welcome }) => {
        if (!cancelled) setCanCreateDossier(canManageDossiers(selectedRole, welcome.roles));
      })
      .catch(() => {
        /* fail-soft: the affordance simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [study_slug, language, selectedRole]);

  // Page self-guard: a KNOWN non-investigator role bounces to its landing tab
  // (the target can never be suivi again — loop-impossible). Unknown role =
  // loader, never a redirect.
  if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) {
    return <Redirect to={getDefaultTabPath(study_slug, selectedRole.name)} />;
  }

  const groups = data?.groups ?? [];
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? groups.filter((group) =>
        (group.external_id ?? group.group_key).toLowerCase().includes(needle)
      )
    : groups;

  const toTreat = filtered.filter(
    (group) => group.to_fill_count > 0 || group.awaiting_subject_count > 0
  );
  const upToDate = filtered.filter(
    (group) => group.to_fill_count === 0 && group.awaiting_subject_count === 0
  );

  const groupLabel = (group: SuiviGroup): string => {
    if (group.external_id) return t("suivi_dossier", { code: group.external_id });
    if (group.members.length === 1 && group.members[0].role_label) {
      return group.members[0].role_label;
    }
    return t("suivi_family");
  };

  // The derived action line — facts come from the API, wording lives here (i18n).
  const actionLine = (group: SuiviGroup): string => {
    const action = group.next_action;
    if (!action) return "";
    if (action.kind === "FILL") {
      return t(action.count > 1 ? "suivi_action_fill_plural" : "suivi_action_fill", {
        count: action.count,
      });
    }
    return action.subject_role_label
      ? t("suivi_action_remind_role", { role: action.subject_role_label })
      : t("suivi_action_remind");
  };

  // Horizon when nothing needs handling: the earliest upcoming window — derived
  // from the SEARCH-FILTERED list so an active search never names an unrelated
  // dossier in the sentence.
  const nextHorizon = filtered
    .filter((group) => group.next_window)
    .sort(
      (first, second) =>
        new Date(first.next_window!.opens_at).getTime()
        - new Date(second.next_window!.opens_at).getTime()
    )[0];

  const openGroup = (group: SuiviGroup): void => {
    ionRouter.push(
      `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(group.group_key)}`,
      "forward",
      "push"
    );
  };

  return (
    <ResonanceLayout>
      <Page>
        {isError ? (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
          />
        ) : data === null ? (
          <ResonanceLoader />
        ) : (
          <>
            <CaptionLine>
              {toTreat.length > 0 ? (
                <>
                  <b>{t("suivi_caption_todo", { count: toTreat.length })}</b>
                  {" · "}
                  {t("suivi_caption_uptodate", { count: upToDate.length })}
                </>
              ) : (
                t("suivi_caption_all_done", { count: groups.length })
              )}
            </CaptionLine>

            <SearchRow>
              <Icon icon={searchOutline} />
              <SearchInput
                type="search"
                value={query}
                placeholder={t("suivi_search_placeholder")}
                onChange={(e) => setQuery(e.target.value)}
              />
            </SearchRow>

            {canCreateDossier && (
              <NewDossierRow>
                <OutlinePill type="button" onClick={() => setCreateOpen(true)}>
                  + {t("suivi_new_dossier_button")}
                </OutlinePill>
              </NewDossierRow>
            )}

            {filtered.length === 0 ? (
              <EmptyText>
                {needle
                  ? t("suivi_search_no_match", { query: query.trim() })
                  : t("suivi_empty")}
              </EmptyText>
            ) : (
              <>
                {toTreat.length > 0 && (
                  <>
                    <SectionLabel>
                      {t("suivi_group_todo_title")} ({toTreat.length})
                    </SectionLabel>
                    <ListCard>
                      {toTreat.map((group) => (
                        <Row
                          key={group.group_key}
                          as="button"
                          type="button"
                          $tappable
                          onClick={() => openGroup(group)}
                        >
                          <RowMain>
                            <RowTitle>{groupLabel(group)}</RowTitle>
                            <RowSub>{actionLine(group)}</RowSub>
                          </RowMain>
                          <RowEnd>
                            {group.worst_end_date && (
                              <DueChip endDate={group.worst_end_date} t={t} />
                            )}
                          </RowEnd>
                          <Chevron>›</Chevron>
                        </Row>
                      ))}
                    </ListCard>
                  </>
                )}

                {toTreat.length === 0 && nextHorizon?.next_window && (
                  <FooterLine>
                    {t("suivi_home_next_window", {
                      date: dayjs(nextHorizon.next_window.opens_at).format("DD/MM/YYYY"),
                      group: groupLabel(nextHorizon),
                    })}
                  </FooterLine>
                )}

                {upToDate.length > 0 && (
                  <ListCard>
                    <FoldRow
                      type="button"
                      onClick={() => setUpToDateOpen((open) => !open)}
                    >
                      <span>{t("suivi_group_uptodate_title")}</span>
                      <FoldCount>
                        {upToDate.length}
                        <Icon
                          icon={upToDateOpen ? chevronUpOutline : chevronDownOutline}
                          size="small"
                        />
                      </FoldCount>
                    </FoldRow>
                    {upToDateOpen &&
                      upToDate.map((group) => (
                        <Row
                          key={group.group_key}
                          as="button"
                          type="button"
                          $tappable
                          onClick={() => openGroup(group)}
                        >
                          <RowMain>
                            <RowTitle>{groupLabel(group)}</RowTitle>
                            <RowSub>{t("suivi_up_to_date_row")}</RowSub>
                          </RowMain>
                          <Chevron>›</Chevron>
                        </Row>
                      ))}
                  </ListCard>
                )}

              </>
            )}
          </>
        )}

        <CreateDossierModal
          isOpen={createOpen}
          onDismiss={() => setCreateOpen(false)}
          onCreated={(groupKey) => {
            setCreateOpen(false);
            fetchGroups();
            ionRouter.push(
              `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(groupKey)}`,
              "forward",
              "push"
            );
          }}
        />
      </Page>
    </ResonanceLayout>
  );
};

const NewDossierRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const SearchRow = styled.div`
  ${cardSurface}
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: var(--app-color-surface-tint, white);
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.55);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: var(--app-color-text, #0f172a);

  &::placeholder {
    color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.45);
  }
`;

const FoldRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const FoldCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.45);
`;

export default SuiviHome;
